import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import EditFileModal from 'widgets/files/editFileModal';
import { UploadedFileOrString } from 'shared/lib/utils/uploadedFileOrString';
import DeleteFileModalController, {
  DeleteFileModalControllerActions,
} from 'widgets/tables/deleteFileModalController';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { AxiosError } from 'axios';
import { BackErrors } from 'shared/lib/utils/globalUtils';
import ErrorModal from 'shared/lib/components/modals/errorModal';
import {
  FileUpdatable,
  UpdateFileHelper,
} from 'entities/file/model/updateFileHelper';
import { getAbsPath } from 'app/api/api';

export type EditFileModalActions = {
  afterFileUpdate?: (updatable: FileUpdatable) => void;
} & DeleteFileModalControllerActions;

export default observer(function EditFileModalController({
  updatable,
  afterFileDelete,
  afterFileUpdate,
  handleClose,
}: {
  updatable: FileUpdatable;
  handleClose: () => void;
} & EditFileModalActions) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { mobxStore } = useContext(MobxContext);
  const { t } = useTranslation();

  const [isDublicateError, setIsDublicateError] = useState(false);

  return (
    <>
      <EditFileModal
        isOpen={true}
        handleClose={handleClose}
        initialSectionId={updatable.section_id}
        initialFolderId={updatable.folder_id}
        fileName={updatable.name}
        preview={
          updatable.preview_url
            ? new UploadedFileOrString(getAbsPath(updatable.preview_url)!)
            : null
        }
        onDelete={() => setIsDeleting(true)}
        onSubmit={async (params) => {
          try {
            const updateHelper = new UpdateFileHelper(updatable);

            const newData = await updateHelper.update({
              id: updatable.id,
              name: params.name,
              folder_id: params.folderId,
              section_id: params.sectionId,
              previewFile: params.preview
                ? params.preview.file || undefined
                : null,
            });

            afterFileUpdate && afterFileUpdate(newData);
          } catch (e) {
            const err = e as AxiosError<{ detail?: string }>;
            console.error(e);

            if (
              err?.response?.status === 400 &&
              err?.response?.data?.detail === BackErrors.FileExists
            ) {
              setIsDublicateError(true);
              return;
            }

            mobxStore.notificationsStore.errorMessage = t(
              'notifications.errors.default'
            );
          }

          handleClose();
        }}
      ></EditFileModal>

      {isDeleting && (
        <DeleteFileModalController
          id={updatable.id}
          handleClose={() => {
            setIsDeleting(false);
            handleClose();
          }}
          afterFileDelete={afterFileDelete}
        ></DeleteFileModalController>
      )}

      {isDublicateError && (
        <ErrorModal
          mainText="Ошибка действия"
          isOpen={true}
          onClose={() => setIsDublicateError(false)}
        >
          <Typography variant="f18" sx={{ fontWeight: 300 }}>
            Действие невозможно&nbsp;&mdash; объект с&nbsp;таким названием уже
            существует по&nbsp;указанному адресу
          </Typography>
        </ErrorModal>
      )}
    </>
  );
});
